import { Popover, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { circleApi } from 'src/api/circles';
import { SubscriptionStatus, subscriptionsApi } from 'src/api/subscriptions';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { useCircleRoleView } from 'src/hooks/CircleRoleView/useCircleRoleView';
import { selectCircleDetailsData } from 'src/redux/slices/circleDetailsSlice/circleDetailsSlice';
import { removeCircleById } from 'src/redux/slices/loggedUserSlice';
import { setActiveTabIndex } from 'src/redux/slices/tabsSlice';
import { themeColors } from 'src/theme';
import { UnsubscribeModal } from '../UserInfo/Modals/UnsubscribeFromCircle/UnsubscribeFromCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { DevelopmentPlan } from 'src/pages/DevelopmentPlan/DevelopmentPlan';
import { Analytics } from '../Analytics/Analytics';
import { TabPanelProps, TabsAuctionProps } from '../const';
import { OthCommunicationTab } from '../OthCommunication/OthCommunicationTab';
import { Information } from '../Information/Information';
import { OthTab } from '../Oth/OthTab';
import { Oth360Tab } from '../Oth360Tab/Oth360Tab';
import { Oth360AdminTab } from '../Oth360AdminTab/Oth360AdminTab';
import { AssessmentDialog } from '../Assessment/AssessmentDialog';
import { AssessmentTab } from '../Assessment/AssessmentTab';
import { TAB_LABEL as ASSESSMENT_TAB_LABEL } from '../Assessment/assessmentText';
import { CircleMessages } from './Messages/CircleMessages';
import { TabActiveContext } from './tabActiveContext';
import { requestedTabIndex } from './tabByLabel';
import { Videos } from './Videos/Videos';

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      <Box
        sx={{
          p: 3,
          display: isActive ? 'block' : 'none',
        }}
      >
        <TabActiveContext.Provider value={isActive}>
          {children}
        </TabActiveContext.Provider>
      </Box>
    </div>
  );
}

export default function TabsAuction({
  circleId,
  circleName,
  videoMessages,
}: TabsAuctionProps) {
  const isNarrowMobile = useMediaQuery('(max-width: 545px)');
  const circleData = useAppSelector(selectCircleDetailsData);
  const externalTab = useAppSelector((state) => state.tabs.activeTabIndex);
  const dispatch = useAppDispatch();

  const { isLeader, actualIsLeader } = useCircleRoleView(circleId);
  const loadingCircleMessages = useAppSelector(
    (state) => state.circleDetail.loadingCircleMessages
  );
  const specialCode = circleData?.special_code?.trim().toUpperCase();
  const isOthEnabled = specialCode === 'OTH';
  // The loneliness programme (docs/briefs/dunbar148-loneliness-plan.md): the
  // measure's tab for every role, and the "before" questions for members
  const isLonelinessEnabled = specialCode === 'LONELINESS';
  const [assessmentRefreshKey, setAssessmentRefreshKey] = useState(0);
  // The circle is connected to a 360review account (Edit Circle, OTH only)
  const isReview360Enabled = Boolean(circleData?.integrations?.review360);

  const [value, setValue] = useState(0);
  const [overflowAnchorEl, setOverflowAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [isOpenUnsubscribeModal, setIsOpenUnsubscribeModal] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabDragRef = useRef({
    isDragging: false,
    didDrag: false,
    startX: 0,
    startScrollLeft: 0,
  });

  useEffect(() => {
    const scroller = tabsContainerRef.current?.querySelector(
      '.MuiTabs-scroller'
    ) as HTMLElement | null;
    if (!scroller) {
      return;
    }

    const DRAG_THRESHOLD = 6;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        return;
      }
      tabDragRef.current = {
        isDragging: true,
        didDrag: false,
        startX: event.clientX,
        startScrollLeft: scroller.scrollLeft,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const drag = tabDragRef.current;
      if (!drag.isDragging) {
        return;
      }
      const deltaX = event.clientX - drag.startX;
      if (!drag.didDrag && Math.abs(deltaX) <= DRAG_THRESHOLD) {
        return;
      }
      drag.didDrag = true;
      scroller.style.cursor = 'grabbing';
      scroller.style.userSelect = 'none';
      scroller.scrollLeft = drag.startScrollLeft - deltaX;
    };

    const endDrag = () => {
      const drag = tabDragRef.current;
      if (!drag.isDragging) {
        return;
      }
      drag.isDragging = false;
      scroller.style.cursor = 'grab';
      scroller.style.userSelect = '';
    };

    const onClickCapture = (event: Event) => {
      if (!tabDragRef.current.didDrag) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      tabDragRef.current.didDrag = false;
    };

    scroller.style.cursor = 'grab';
    scroller.addEventListener('pointerdown', onPointerDown);
    scroller.addEventListener('pointermove', onPointerMove);
    scroller.addEventListener('pointerup', endDrag);
    scroller.addEventListener('pointercancel', endDrag);
    scroller.addEventListener('click', onClickCapture, true);

    return () => {
      scroller.removeEventListener('pointerdown', onPointerDown);
      scroller.removeEventListener('pointermove', onPointerMove);
      scroller.removeEventListener('pointerup', endDrag);
      scroller.removeEventListener('pointercancel', endDrag);
      scroller.removeEventListener('click', onClickCapture, true);
      scroller.style.cursor = '';
      scroller.style.userSelect = '';
    };
  }, [isLeader, isOthEnabled, isLonelinessEnabled]);

  const handleUnsubscribe = async () => {
    try {
      const subscriptions = await subscriptionsApi.getSubscriptions();
      const activeSubscriptionsForCircle = subscriptions.filter(
        (subscription) =>
          subscription.tribe_id === circleId &&
          subscription.status === SubscriptionStatus.ACTIVE
      );

      await Promise.all(
        activeSubscriptionsForCircle.map((subscription) =>
          subscriptionsApi.cancelSubscription(subscription._id)
        )
      );

      await circleApi.leaveCircle(circleId);
      dispatch(removeCircleById(circleId));
      toast.success(
        'You have successfully left the circle and stopped recurring billing.'
      );
      navigate(PathRoutes.ProfileRoute);
    } catch (error) {
      console.error('Failed to unsubscribe from circle:', error);
      toast.error(
        'Failed to leave the circle and stop billing. Please try again.'
      );
    }
  };

  const tabList = [
    { label: 'Challenges', component: <Videos circleId={circleId} /> },
    ...(isLeader
      ? [
          {
            label: 'Analytics',
            component: <Analytics circleId={circleId} />,
          },
        ]
      : []),
    {
      label: 'Information',
      component: <Information circleId={circleId} />,
    },
    // Leaders see the plan too, as in the app, so they know what their
    // participants see (Markus, 2026-10-29)
    ...(isOthEnabled
      ? [
          {
            label: 'Utviklingsplan',
            component: <DevelopmentPlan embedded circleId={circleId} />,
          },
        ]
      : []),
    ...(!isLeader && isOthEnabled && isReview360Enabled
      ? [
          {
            label: '360',
            component: <Oth360Tab circleId={circleId} />,
          },
        ]
      : []),
    ...(isLonelinessEnabled
      ? [
          {
            label: ASSESSMENT_TAB_LABEL,
            component: (
              <AssessmentTab
                circleId={circleId}
                isLeader={isLeader}
                refreshKey={assessmentRefreshKey}
              />
            ),
          },
        ]
      : []),
    ...(isLeader && isOthEnabled
      ? [
          {
            label: 'Participants',
            component: (
              <OthTab
                circleId={circleId}
                circleName={circleName}
                isLeader={isLeader}
                review360Enabled={isReview360Enabled}
              />
            ),
          },
          ...(isReview360Enabled
            ? [
                {
                  label: '360',
                  component: <Oth360AdminTab circleId={circleId} />,
                },
              ]
            : []),
          {
            label: 'Communication',
            component: <OthCommunicationTab circleId={circleId} />,
          },
        ]
      : []),
    {
      label: 'Messages',
      component: (
        <CircleMessages
          videoMessages={videoMessages}
          isLeader={actualIsLeader}
          loading={loadingCircleMessages}
        />
      ),
    },
  ];

  useEffect(() => {
    if (typeof externalTab !== 'number') {
      return;
    }
    setValue(
      externalTab >= 0 && externalTab < tabList.length ? externalTab : 0
    );
  }, [externalTab, tabList.length]);

  // A deep link names a tab by label (`?tab=360`, from 360review's emails):
  // selected once per circle page, in whichever view the user has. Without
  // the parameter, or without such a tab, nothing changes.
  const tabLabelsKey = tabList.map((tab) => tab.label).join('\n');
  const appliedDeepLink = useRef<string | null>(null);
  useEffect(() => {
    const key = `${circleId}|${search}`;
    if (appliedDeepLink.current === key) {
      return;
    }
    appliedDeepLink.current = key;
    const index = requestedTabIndex(search, tabLabelsKey.split('\n'));
    if (index !== null) {
      dispatch(setActiveTabIndex(index));
    }
  }, [circleId, search, tabLabelsKey, dispatch]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    dispatch(setActiveTabIndex(newValue));
  };

  return (
    <Box
      sx={{
        width: '100%',
        marginTop: 0,
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          position: isNarrowMobile ? 'sticky' : 'static',
          top: isNarrowMobile ? '56px' : 'auto',
          zIndex: isNarrowMobile ? 1000 : 'auto',
          // In a branded circle the tab bar belongs to the header zone
          backgroundColor:
            'var(--brand-header-bg, var(--brand-background, #fff))',
          color: 'var(--brand-header-text, inherit)',
          paddingLeft: 'var(--brand-header-inset, 0px)',
          boxShadow: isNarrowMobile
            ? '0 2px 6px rgba(22, 24, 35, 0.08)'
            : 'none',
        }}
      >
        <Tabs
          ref={tabsContainerRef}
          value={value}
          onChange={handleChange}
          aria-label="circle tabs"
          TabIndicatorProps={{
            sx: {
              background: `var(--brand-accent, ${themeColors.text.dark100})`,
            },
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& button.Mui-selected': {
              color: `var(--brand-header-text, var(--brand-accent, ${themeColors.text.dark100}))`,
            },
            '& button': {
              color: `var(--brand-header-muted, ${themeColors.text.dark70})`,
              fontFamily: 'var(--brand-font, inherit)',
              textTransform: 'var(--brand-tab-transform, uppercase)',
              fontSize: 'var(--brand-tab-size, 0.875rem)',
            },
          }}
        >
          {tabList.map((tab, index) => (
            <Tab key={index} label={tab.label} id={`simple-tab-${index}`} />
          ))}
        </Tabs>
        {!isLeader && (
          <>
            <button
              onClick={(e) => setOverflowAnchorEl(e.currentTarget)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '8px',
                flexShrink: 0,
              }}
            >
              <MoreVertIcon
                sx={{
                  color: 'var(--brand-header-text, #161823)',
                  fontSize: 24,
                }}
              />
            </button>
            <Popover
              open={Boolean(overflowAnchorEl)}
              anchorEl={overflowAnchorEl}
              onClose={() => setOverflowAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 160,
                  padding: '0',
                }}
              >
                <div
                  onClick={() => {
                    setOverflowAnchorEl(null);
                    setIsOpenUnsubscribeModal(true);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 16px',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#f0544f',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#fef2f2')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <ExitToAppIcon sx={{ fontSize: 18, color: '#f0544f' }} />
                  Leave Circle
                </div>
              </div>
            </Popover>
          </>
        )}
      </Box>
      {tabList.map((tab, index) => (
        <CustomTabPanel key={index} value={value} index={index}>
          {tab.component}
        </CustomTabPanel>
      ))}

      <UnsubscribeModal
        isOpenModal={isOpenUnsubscribeModal}
        setIsOpenModal={setIsOpenUnsubscribeModal}
        handleUnsubscribe={handleUnsubscribe}
      />
      {isLonelinessEnabled && !isLeader ? (
        <AssessmentDialog
          circleId={circleId}
          onAnswered={() => setAssessmentRefreshKey((key) => key + 1)}
        />
      ) : null}
    </Box>
  );
}
